import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setLeadOutcome } from '../../../api/leadsApi';
import { updateCustomer } from '../../../api/customersApi';
import { createFollowUpTask } from '../../../api/communications';
import './InboxActionRail.css';

// Where campaign replies land + where the team works the conversation. First-
// class actions for leads AND customers — never gated on object==="lead".
const STAGES = ['NEW', 'CONTACTED', 'QUALIFIED', 'QUOTED', 'WON', 'LOST'];

export default function InboxActionRail({ active, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState('');

  if (!active) return null;
  const leadId = active.lead_id;
  const customerId = active.customer_id;
  const who = active.contact_name || active.display_name || active.contact_phone || 'this contact';

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setNote(''); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const advance = (stage) => {
    if (!leadId) { toast.info('Stage applies to leads only'); return; }
    run(() => setLeadOutcome(leadId, { stage, outcome_note: note || undefined }), `Moved to ${stage}`);
  };

  const notInterested = () => run(async () => {
    if (leadId) await setLeadOutcome(leadId, { do_not_contact: true, outcome_note: note || 'Not interested' });
    else if (customerId) await updateCustomer(customerId, { do_not_contact: true });
    else throw new Error('no target');
  }, `${who} marked not interested — suppressed from future outreach`);

  const addFollowUp = () => {
    if (!followUp) return;
    // Task FK fields are named lead/customer (not lead_id/customer_id) — the
    // serializer ignores *_id and then rejects the task as unattached.
    run(() => createFollowUpTask({
      lead: leadId || undefined,
      customer: customerId || undefined,
      title: `Follow up: ${who}`,
      description: note || undefined,
      task_type: 'follow_up',
      due_date: new Date(`${followUp}T09:00:00`).toISOString(),
    }), `Follow-up set for ${followUp}`);
  };

  // The note must persist on its own, not only bundled into a task.
  const saveNote = () => {
    if (!note.trim()) { toast.info('Nothing to save'); return; }
    if (leadId) run(() => setLeadOutcome(leadId, { outcome_note: note }), 'Note saved');
    // Customer has no standalone note field — persist as a note-type task.
    else if (customerId) run(() => createFollowUpTask({
      customer: customerId, title: note.slice(0, 120), task_type: 'note',
    }), 'Note saved');
    else toast.info('No lead or customer on this conversation');
  };

  return (
    <div className="iar-card">
      <div className="iar-title">Work this conversation</div>

      {leadId ? (
        <div className="iar-block">
          <div className="iar-label">Advance stage</div>
          <div className="iar-stages">
            {STAGES.map((s) => (
              <button key={s} className={`iar-stage${s === 'LOST' ? ' iar-stage--lost' : ''}`}
                      disabled={busy} onClick={() => advance(s)}>{s}</button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="iar-block">
        <div className="iar-label">Outcome note</div>
        <textarea className="iar-note" rows={2} value={note} disabled={busy}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Applies to the stage change or the actions below" />
        <button className="iar-btn iar-note-save" disabled={busy || !note.trim()} onClick={saveNote}>
          Save note
        </button>
      </div>

      <div className="iar-block">
        <div className="iar-label">Follow-up</div>
        <div className="iar-row">
          <input type="date" className="iar-date" value={followUp} disabled={busy}
                 onChange={(e) => setFollowUp(e.target.value)} />
          <button className="iar-btn" disabled={busy || !followUp} onClick={addFollowUp}>Set task</button>
        </div>
      </div>

      <div className="iar-block iar-actions">
        <button className="iar-btn iar-danger" disabled={busy} onClick={notInterested}>Not interested</button>
        {customerId ? (
          <button className="iar-btn" onClick={() => navigate(`/customers/${customerId}`)}>Open customer</button>
        ) : null}
        {leadId ? (
          <button className="iar-btn" onClick={() => navigate(`/leads/${leadId}`)}>Open lead</button>
        ) : null}
      </div>
    </div>
  );
}
